// ============================================================================
// DUPLICATED LOGIC: also implemented in backend/app/public/spread.py.
// Change both and update the parity fixtures in shared/vectors/spread.json.
// Why: the public API resolves spread rates on the server (ADR 0003 amendment).
// CI runs the same fixtures in frontend/src/lib/spread.vectors.test.ts and
// backend/worker_tests/test_spread_vectors.py. A change to one side alone fails CI.
// ============================================================================
// WHICH Rx A SPREAD SPENDS: "they automatically use the most recent Rx for those nutrients, and to
// customize to pick a different rx.
import { assembleFertLayers } from "./fertility";
import type { ManifestLayer } from "../types/contract";
import { loadRecSets, type SavedRecSet } from "./fertSets";
import {
  frozenRateColumn,
  frozenSpread,
  isFrozen,
  OPEN_FAILED,
  openRecDocument,
  type FrozenNutrient,
} from "./frozenRx";
import { assertCurrentGrid, readyRecSet } from "./rxGrid";

export type SpreadRxNutrient = "lime" | "p2o5" | "k2o";

// Lime, then phosphorus, then potassium. The chooser and the editor share this order.
export const SPREAD_RX_NUTRIENTS: SpreadRxNutrient[] = ["lime", "p2o5", "k2o"];

// Nutrients every selected field has at least one saved Rx for.
export function sharedNutrients(fieldIds: string[]): SpreadRxNutrient[] {
  if (!fieldIds.length) return [];
  return SPREAD_RX_NUTRIENTS.filter((nutrient) =>
    fieldIds.every((id) => setsFor(id, nutrient).length > 0),
  );
}

export interface ResolvedSpreadRx {
  set: SavedRecSet;
  /* * per-cell PURE nutrient — lime in tons/ac CaCO₃, P/K in lbs/ac of P₂O₅ / K₂O */
  rates: (number | null)[];
  geoms: { type?: string; coordinates?: unknown }[];
  /** Saved grid is not the field's current boundary. Not the file's clipped flag. */
  notice: string | null;
  /** The spread file is clipped because the saved boundary is not current. */
  fileNotice: string | null;
}

/* * Every saved set carrying this nutrient, newest first. */
export function setsFor(
  fieldId: string,
  nutrient: SpreadRxNutrient,
  from?: SavedRecSet[],
): SavedRecSet[] {
  // `from` = a caller-held list (e.g. `hydrateFertRx`'s RETURN value — the answer, which the
  // localStorage mirror may under-represent on a full origin). Absent → the cache, as before.
  return (from ?? loadRecSets(fieldId))
    .filter((s) => (s.nutrients ?? []).includes(nutrient))
    .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));
}

/* * The most recent saved Rx for this nutrient, or null when the field has none. */
export function newestSetFor(
  fieldId: string,
  nutrient: SpreadRxNutrient,
  from?: SavedRecSet[],
): SavedRecSet | null {
  return setsFor(fieldId, nutrient, from)[0] ?? null;
}

export async function resolveSpreadRx(
  fieldId: string,
  nutrient: SpreadRxNutrient,
  set: SavedRecSet,
): Promise<ResolvedSpreadRx> {
  const opened = await openRecDocument(fieldId, set);
  if (isFrozen(opened.set)) {
    const spread = await frozenSpread(fieldId, opened);
    const rates = frozenRateColumn(
      opened.set,
      nutrient as FrozenNutrient,
      spread.geoms.length,
    );
    const listed = (opened.set.nutrients ?? []).includes(nutrient);
    if (!rates && listed) throw new Error(OPEN_FAILED);
    if (!rates || !rates.some((rate) => rate != null)) {
      throw new Error(
        `That Rx doesn’t include ${nutrient === "lime" ? "lime" : nutrient === "p2o5" ? "phosphorus" : "potassium"}.`,
      );
    }
    return {
      set: opened.set,
      rates,
      geoms: spread.geoms,
      notice: spread.notice,
      fileNotice: spread.fileNotice,
    };
  }
  const ready = await readyRecSet(fieldId, opened.set);
  const cell = await assertCurrentGrid(fieldId, ready);
  // Not `computeRecs`: for P and K the number to spread is the TOTAL, which folds in crop removal
  // and the BCSR cation correction, and only `assembleFertLayers` builds it.
  const { layers } = assembleFertLayers(cell.geoms, ready.soil, {
    nutrients: ready.nutrients,
    cation: ready.cation,
    replacement: ready.replacement ?? null,
    replP: ready.replP ?? true,
    replK: ready.replK ?? true,
    cationRatios:
      ready.cationCaMg != null && ready.cationMgK != null
        ? { caMg: ready.cationCaMg, mgK: ready.cationMgK }
        : undefined,
  });
  // Prefer the nutrient's TOTAL when the set built one — that is the layer carrying every correction.
  const layer =
    layers.find((l) => l.id === `fert-${nutrient}-total`) ??
    layers.find((l) => l.id === `fert-${nutrient}`);
  if (!layer)
    throw new Error(
      `That Rx doesn’t include ${nutrient === "lime" ? "lime" : nutrient === "p2o5" ? "phosphorus" : "potassium"}.`,
    );
  return {
    set: ready,
    rates: ratesFromLayer(layer, cell.geoms),
    geoms: cell.geoms,
    notice: null,
    fileNotice: null,
  };
}

/*
 *
 * It sets `geometry` to the very object from the `geoms` array, so identity ties each feature back
 * to its cell exactly, in one pass.
 */
export function ratesFromLayer(
  layer: ManifestLayer,
  geoms: { type?: string; coordinates?: unknown }[],
): (number | null)[] {
  const index = new Map<unknown, number>();
  geoms.forEach((g, i) => index.set(g, i));
  const out: (number | null)[] = new Array(geoms.length).fill(null);
  const fc = layer.inline as unknown as
    | { features?: { properties?: { value?: unknown }; geometry?: unknown }[] }
    | undefined;
  for (const f of fc?.features ?? []) {
    const i = index.get(f.geometry);
    const v = f.properties?.value;
    if (i != null && typeof v === "number" && Number.isFinite(v)) out[i] = v;
  }
  return out;
}
